// ============================================
// MODALFUSION.JS — Confirmar fusión de 2 entidades (grupo/razón social/persona)
// ============================================

import React, { useMemo, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';

const CAMPOS_POR_TIPO = {
  grupo: [
    { k: 'nombre', label: 'Nombre' },
    { k: 'notas', label: 'Notas' },
  ],
  razon_social: [
    { k: 'razon_social', label: 'Razón social' },
    { k: 'rfc', label: 'RFC' },
    { k: 'regimen_sat', label: 'Régimen SAT' },
    { k: 'domicilio_fiscal', label: 'Domicilio fiscal' },
    { k: 'codigo_postal_fiscal', label: 'CP fiscal' },
    { k: 'email_facturacion', label: 'Email facturación' },
    { k: 'metodo_pago_preferido', label: 'Método de pago' },
  ],
  persona: [
    { k: 'nombre_completo', label: 'Nombre' },
    { k: 'celular', label: 'Celular' },
    { k: 'email', label: 'Email' },
    { k: 'email_alternativo', label: 'Email alt.' },
    { k: 'rfc', label: 'RFC' },
    { k: 'curp', label: 'CURP' },
    { k: 'direccion', label: 'Dirección' },
    { k: 'notas', label: 'Notas' },
  ],
};

const LABEL_TIPO = { grupo: 'grupos (clientes)', razon_social: 'razones sociales', persona: 'personas' };

export default function ModalFusion({
  tipo, conservada, absorbida,
  resumenConservada, resumenAbsorbida,
  onCerrar, onCompletado,
}) {
  const [motivo, setMotivo] = useState('');
  const [resolucion, setResolucion] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const vacio = (v) => v == null || v === '';

  const conflictos = useMemo(() => {
    const campos = CAMPOS_POR_TIPO[tipo] || [];
    return campos.filter(({ k }) =>
      !vacio(conservada[k]) && !vacio(absorbida[k]) && conservada[k] !== absorbida[k]
    );
  }, [tipo, conservada, absorbida]);

  const confirmar = async () => {
    setGuardando(true); setError(null);
    try {
      const body = {
        tipo,
        conservar_id: conservada.id,
        absorber_id: absorbida.id,
        resolucion_conflictos: resolucion,
        motivo: motivo || null,
      };
      const r = await fetch(`${API_BASE_CLI}/api/clientes/fusionar`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'Error');
      onCompletado(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCerrar}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 540,
        fontFamily: PP, color: COLOR_GRIS, maxHeight: '92vh', overflowY: 'auto',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 16 }}>
          🔗 Fusionar {LABEL_TIPO[tipo] || 'entidades'}
        </div>

        <div style={{
          padding: 14, border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, marginBottom: 10,
          background: '#eaf2fb',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 4 }}>● Se conservará:</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>
            {etiquetaEntidad(tipo, conservada)}
          </div>
          {resumenConservada && <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>{resumenConservada}</div>}
        </div>

        <div style={{
          padding: 14, border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, marginBottom: 14,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, marginBottom: 4 }}>○ Se absorberá:</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_GRIS }}>
            {etiquetaEntidad(tipo, absorbida)}
          </div>
          {resumenAbsorbida && <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>{resumenAbsorbida}</div>}
        </div>

        <div style={{
          padding: 12, background: '#fff8ed', borderRadius: 8,
          fontSize: 12, color: '#8a6d1e', lineHeight: 1.6, marginBottom: 14,
        }}>
          <strong>Qué pasará:</strong><br />
          {tipo === 'razon_social' && '· Sucursales y contactos de la absorbida pasan a la conservada.'}
          {tipo === 'grupo' && '· Razones sociales, sucursales huérfanas y zonas pasan a la conservada.'}
          {tipo === 'persona' && '· Todos los roles de contacto de la absorbida pasan a la conservada.'}
          <br />· La absorbida queda archivada (no se borra).
          <br />· Se registra en el log de fusiones (reversible en revisión manual).
        </div>

        {conflictos.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#a86b1e', marginBottom: 8 }}>
              ⚠️ Conflictos detectados ({conflictos.length}):
            </div>
            {conflictos.map(c => (
              <div key={c.k} style={{
                padding: 10, border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
                marginBottom: 6,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLOR_OSCURO, marginBottom: 6 }}>{c.label}</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', marginBottom: 4 }}>
                  <input
                    type="radio"
                    name={`r-${c.k}`}
                    checked={(resolucion[c.k] || 'a') === 'a'}
                    onChange={() => setResolucion(r => ({ ...r, [c.k]: 'a' }))}
                  />
                  <span>Conservar: <strong>{String(conservada[c.k])}</strong></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name={`r-${c.k}`}
                    checked={resolucion[c.k] === 'b'}
                    onChange={() => setResolucion(r => ({ ...r, [c.k]: 'b' }))}
                  />
                  <span>Tomar de absorbida: <strong>{String(absorbida[c.k])}</strong></span>
                </label>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Motivo (opcional)</label>
          <input
            style={estiloInput}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: duplicado detectado al editar RFC"
          />
        </div>

        {error && <div style={{ color: '#e05c5c', fontSize: 12, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar}>Cancelar</button>
          <button style={estiloBotonPrimario} onClick={confirmar} disabled={guardando}>
            {guardando ? 'Fusionando…' : 'Confirmar fusión'}
          </button>
        </div>
      </div>
    </div>
  );
}

function etiquetaEntidad(tipo, e) {
  if (!e) return '—';
  if (tipo === 'razon_social') return e.razon_social;
  if (tipo === 'persona') return e.nombre_completo;
  return e.nombre;
}
